import React, { useCallback, useEffect, useState } from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { Link, Redirect } from "react-router-dom";
import { toast } from "react-toastify";
import { Popconfirm } from "antd";
import { faArrowLeft, faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import Spinner from "../../lib/components/Spinner";
import { TYPES } from "./constants";
import Translations from "./translations";
import languages from "../../../data/languages";
import getFilesUrl from "../../../helpers/getFilesUrl";
import { addStories } from "./requests/addStories";
import { addBackground, addLogo } from "./requests/addPhoto";
import { requestGetStoriesList } from "./requests/getStories";
import filterTypes from "../../../data/filter-types";

const NEW_STORY_ID = "new";

const languageTemplate = {
  url: "",
  urlButtonName: "",
  title: "",
  content: "",
  language: "",
  background: {
    id: "",
    node: ""
  },
  logo: {
    id: "",
    node: ""
  },
  options: [],
};

const StoriesSingle = (props) => {
  const {
    getList,
    match: { params }
  } = props;
  const {
    id,
    page
  } = params;

  const [loading, setLoading] = useState(false);
  const [redirectTo, setRedirectTo] = useState("");

  const [active, setActive] = useState(false);
  const [name, setName] = useState("");
  const [country, setCountry] = useState("");
  const [type, setType] = useState(TYPES.LINK);
  const [translations, setTranslations] = useState(Object.keys(languages)
    .map(langKey => ({
      ...languageTemplate,
      language: langKey,
    })));
  const [previewBackground, setPreviewBackground] = useState({});
  const [previewLogo, setPreviewLogo] = useState({});
  const [pages, setPages] = useState([]);
  const [pageNumber, setPageNumber] = useState(Number(page));

  const resetPageData = () => {
    setRedirectTo("");
    setActive(false);
    setName("");
    setCountry("");
    setType(TYPES.LINK);
    setTranslations(Object.keys(languages)
      .map(langKey => ({
        ...languageTemplate,
        language: langKey,
      })));
    setPreviewBackground({});
    setPreviewLogo({});
    setPages([]);
  };

  const handleChangeLogo = (lang, ev) => {
    const file = ev.target.files[0];
    setPreviewLogo({
      ...previewLogo,
      [lang]: {
        file,
        preview: URL.createObjectURL(file),
      },
    });
  };

  const handleChangeBackground = (lang, ev) => {
    const file = ev.target.files[0];
    setPreviewBackground({
      ...previewBackground,
      [lang]: {
        file,
        preview: URL.createObjectURL(file),
      },
    });
  };

  const handleChangeLanguage = (lang, name, value) => {
    const localTranslations = [...translations];

    const languageItemIndex = localTranslations.findIndex(i => i.language === lang);
    const languageItem = (languageItemIndex === -1) ? { ...languageTemplate } : { ...localTranslations[languageItemIndex] };

    languageItem.language = lang;
    languageItem[name] = value;

    setTranslations([
      ...translations.filter(i => i.language !== lang),
      languageItem
    ]);
  };

  const handleChange = (ev) => {
    const { name: eventName } = ev.target;
    if (eventName === "name") {
      setName(ev.target.value);
    } else if (eventName === "country") {
      setCountry(ev.target.value);
    }
  };

  const handleChangeType = (ev) => {
    setType(ev.target.value);
  };

  const loadPageData = useCallback(() => {
    if (id && id !== NEW_STORY_ID) {
      getList(0, 0, "id,ASC", [{
        fieldName: "id",
        fieldValue: id,
        searchOperation: filterTypes.EQUALS.value
      }], (response) => {
        if (response[0]) {
          const item = response[0];
          setActive(item.active);
          setName(item.name);
          setCountry(item.country);

          if (item.pages && item.pages.length) {
            setPages(item.pages);
          }

          if (item.pages[pageNumber]) {
            const pageData = item.pages[pageNumber];
            setType(pageData.type);

            const loadedTranslations = pageData.translations;
            if (loadedTranslations.length) {
              setTranslations(loadedTranslations);
              setPreviewLogo(Object.keys(languages)
                .reduce((result, langKey) => {
                  const item = loadedTranslations.find(item => item.language === langKey);
                  result[langKey] = {
                    file: false,
                    preview: item && item.logo ? getFilesUrl(item.logo) : "",
                  };
                  return result;
                }, {}));
              setPreviewBackground(Object.keys(languages)
                .reduce((result, langKey) => {
                  const item = loadedTranslations.find(item => item.language === langKey);
                  result[langKey] = {
                    file: false,
                    preview: item && item.background ? getFilesUrl(item.background) : "",
                  };
                  return result;
                }, {}));
            }
          }
        }
      }, setLoading);
    }
  }, [id, pageNumber, getList]);

  const handleSubmit = async () => {
    const pageItems = [
      ...pages.slice(0, pageNumber),
      {
        number: pageNumber,
        translations,
        type,
      },
      ...pages.slice(pageNumber + 1, pages.length),
    ];
    const mainStories = {
      id,
      active,
      name,
      country,
      pages: pageItems
    };
    if (!mainStories.id || mainStories.id === NEW_STORY_ID) {
      delete mainStories.id;
    }

    setLoading(true);
    try {
      const result = await addStories(mainStories);
      const storiesId = result && result.data ? result.data.content.id : id;

      // eslint-disable-next-line no-restricted-syntax
      for (const langKey in previewLogo) {
        if (previewLogo[langKey].file) {
          // eslint-disable-next-line no-await-in-loop
          await addLogo(storiesId, pageNumber, langKey, previewLogo[langKey].file);
        }
      }

      // eslint-disable-next-line no-restricted-syntax
      for (const langKey in previewBackground) {
        if (previewBackground[langKey].file) {
          // eslint-disable-next-line no-await-in-loop
          await addBackground(storiesId, pageNumber, langKey, previewBackground[langKey].file);
        }
      }

      toast("Story saved successfully", {
        type: toast.TYPE.SUCCESS,
      });

      if (id === NEW_STORY_ID) {
        setRedirectTo(`/stories/${storiesId}/0`);
        window.location.reload();
      } else {
        loadPageData();
      }
    } catch (error) {
      toast(error, {
        type: toast.TYPE.ERROR,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRemovePage = async () => {
    const pageItems = [
      ...pages.slice(0, pageNumber),
      ...pages.slice(pageNumber + 1, pages.length),
    ];
    const mainStories = {
      id,
      active,
      name,
      country,
      pages: pageItems
    };
    if (!mainStories.id || mainStories.id === NEW_STORY_ID) {
      delete mainStories.id;
    }

    setLoading(true);
    try {
      await addStories(mainStories);
      setRedirectTo("/stories");
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPageNumber(Number(page));
    resetPageData();
  }, [page]);

  useEffect(() => {
    loadPageData();
  }, [loadPageData]);

  if (redirectTo) {
    return <Redirect to={redirectTo}/>;
  }

  return (
    <div className="m-2 position-relative">
      {!loading || <Spinner/>}

      <div>
        <h3 className="mb-1"><b>{name || "_"}</b></h3>
        <div className="d-flex justify-content-between align-items-center">
          <div className="align-items-center d-flex">
            <div style={{ width: 100 }}>Page: {pageNumber + 1} {pages.length ? `of ${pages.length}` : ""}</div>
            {id !== NEW_STORY_ID ? (
              <div className="ml-2">
                <Link
                  className="btn btn-info btn-sm mr-2"
                  to={`/stories/${id}/${pageNumber - 1}`}
                  disabled={pageNumber <= 0}
                  title="Previous page"
                >
                  {/* Prev page {pageNumber} */}
                  <FontAwesomeIcon icon={faArrowLeft}/>
                </Link>
                <Link
                  className="btn btn-info btn-sm"
                  to={`/stories/${id}/${pageNumber + 1}`}
                  disabled={pageNumber >= pages.length}
                  title="Next page"
                >
                  {/* Page {pageNumber + 2} */}
                  <FontAwesomeIcon icon={faArrowRight}/>
                </Link>
              </div>
            ) : <div/>}
          </div>
          <div className="mb-2">
            <Popconfirm
              title="Are you sure you want to remove this page?"
              onConfirm={handleRemovePage}
              placement="bottomRight"
              okText="Yes"
              cancelText="Cancel"
            >
              <button className="btn btn-danger btn-sm">Remove Page</button>
            </Popconfirm>
          </div>
        </div>
      </div>

      <div className="alert alert-warning" role="alert">
        Please save every step after editing.
      </div>

      <hr/>

      <div className="container">
        <div className="row">
          <div className="col-4">
            <div className="form-group">
              <label>Active</label>
              <select
                className="form-control form-control-sm"
                onChange={ev => setActive(Boolean(+ev.target.value))}
                value={active ? "1" : "0"}
              >
                <option value="1">Yes</option>
                <option value="0">No</option>
              </select>
            </div>
          </div>
          <div className="col-4">
            <div className="form-group">
              <label>Name</label>
              <input
                className="form-control form-control-sm"
                type="text"
                value={name || ""}
                name="name"
                onChange={handleChange}
              />
            </div>
          </div>
          <div className="col-4">
            <div className="form-group">
              <label>Country</label>
              <input
                className="form-control form-control-sm"
                type="text"
                value={country || ""}
                name="country"
                onChange={handleChange}
              />
            </div>
          </div>
        </div>
      </div>

      <hr/>

      <div className="container">

        <div className="row">
          <div className="col-12">
            <div className="form-group">
              <label>Type</label>
              <select
                className="form-control form-control-sm"
                value={type}
                name="type"
                onChange={handleChangeType}>
                <option value={TYPES.LINK}>Link</option>
                <option value={TYPES.QUESTIONNAIRE}>Questionnaire</option>
              </select>
            </div>
          </div>
        </div>

        <div className="row mb-2 border-top">
          <div className="col-12">
            <Translations
              storyType={type}
              onChange={handleChangeLanguage}
              previewBackground={previewBackground}
              onChangeBackground={handleChangeBackground}
              previewLogo={previewLogo}
              onChangeLogo={handleChangeLogo}
              translations={translations}
            />
          </div>
        </div>
      </div>
      <hr/>
      <div className="d-flex justify-content-between mt-2">
        <Link
          className="btn btn-danger mr-2"
          to="/stories"
        >
          Back
        </Link>
        <button
          className="btn btn-success"
          onClick={handleSubmit}
        >
          Save
        </button>
      </div>
    </div>
  );
};

const mapStateToDispatch = dispatch => ({
  getList: bindActionCreators(requestGetStoriesList, dispatch),
});

export default connect(
  null,
  mapStateToDispatch
)(StoriesSingle);



// WEBPACK FOOTER //
// ./src/views/pages/stories/single.js